import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  DollarSign,
  Info,
} from "lucide-react";

// The visualization component for LeetCode 121: Best Time to Buy and Sell Stock
const BuySellStockVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Standard Max Profit",
      array: [7, 1, 5, 3, 6, 4],
    },
    { id: 2, name: "No Profit", array: [7, 6, 4, 3, 1] },
    { id: 3, name: "End Max Profit", array: [2, 4, 1, 7] },
    { id: 4, name: "Start Max Profit", array: [1, 5, 3, 2, 4] },
    { id: 5, name: "Single Element", array: [10] },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [prices, setPrices] = useState(defaultTestCases[0].array);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function maxProfit(prices):", indent: 0 },
    { line: "minPrice = Infinity", indent: 1 },
    { line: "maxProfit = 0", indent: 1 },
    { line: "", indent: 0 },
    { line: "for price in prices:", indent: 1 },
    { line: "// Is this a new best day to buy?", indent: 2 },
    { line: "minPrice = min(minPrice, price)", indent: 2 },
    { line: "", indent: 0 },
    { line: "// What's the profit if we sell today?", indent: 2 },
    { line: "profit = price - minPrice", indent: 2 },
    { line: "maxProfit = max(maxProfit, profit)", indent: 2 },
    { line: "", indent: 0 },
    { line: "return maxProfit", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (prices) => {
    if (!prices || prices.length < 2) {
      return [
        {
          step: 0,
          description: "Not enough days to trade.",
          isFinal: true,
          minPrice: prices[0] ?? null,
          maxProfit: 0,
          buyDay: 0,
          bestBuyDay: -1,
          bestSellDay: -1,
          currentIndex: 0,
        },
      ];
    }

    const allSteps = [];
    let stepCount = 0;

    let minPrice = Infinity;
    let maxProfit = 0;
    let buyDay = -1;
    let bestBuyDay = -1;
    let bestSellDay = -1;

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        minPrice,
        maxProfit,
        buyDay,
        bestBuyDay,
        bestSellDay,
        ...details,
      });
    };

    addStep("Start: Initialize minPrice to Infinity and maxProfit to 0.", 1);

    for (let i = 0; i < prices.length; i++) {
      const price = prices[i];
      addStep(`Processing Day ${i}, Price: ${price}.`, 4, { currentIndex: i });

      if (price < minPrice) {
        minPrice = price;
        buyDay = i;
        addStep(`New minimum price found: ${price} on Day ${i}.`, 6, {
          currentIndex: i,
        });
      } else {
        addStep(`Current minPrice ${minPrice} is still lower.`, 6, {
          currentIndex: i,
        });
      }

      const profit = price - minPrice;
      addStep(
        `Calculate potential profit: ${price} - ${minPrice} = ${profit}.`,
        9,
        { currentIndex: i, currentProfit: profit }
      );

      if (profit > maxProfit) {
        maxProfit = profit;
        bestBuyDay = buyDay;
        bestSellDay = i;
        addStep(`New max profit found: ${maxProfit}.`, 10, {
          currentIndex: i,
          currentProfit: profit,
        });
      } else {
        addStep(
          `Profit ${profit} is not greater than maxProfit ${maxProfit}.`,
          10,
          { currentIndex: i, currentProfit: profit }
        );
      }
    }

    addStep(`Finished. Max profit is ${maxProfit}.`, 12, {
      isFinal: true,
      currentIndex: prices.length - 1,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(prices);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setHighlightedLine(newSteps.length > 0 ? newSteps[0].highlightLine : -1);
  }, [prices]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(steps.length > 0 ? steps[0].highlightLine : -1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setPrices(testCases[index].array);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      if (!Array.isArray(nums) || nums.some(isNaN)) {
        throw new Error("Input must be an array of numbers.");
      }
      const newTestCase = { id: Date.now(), name: `Custom`, array: nums };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid format: ${error.message}. Use comma-separated numbers.`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};
  const maxPrice = Math.max(...prices, 1);
  const chartHeight = 150;

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <DollarSign className="text-blue-500" /> Best Time to Buy and Sell Stock
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                You are given an array `prices` where `prices[i]` is the price
                of a given stock on the `i-th` day. You want to maximize your
                profit by choosing a single day to buy one stock and choosing a
                different day in the future to sell that stock. Return the
                maximum profit you can achieve. If you cannot achieve any
                profit, return 0.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      [{tc.array.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Prices: 7,1,5,3,6,4"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div
              className="flex justify-between items-end border-b-2 border-gray-400 mt-8"
              style={{ height: chartHeight }}
            >
              {prices.map((price, idx) => {
                const height = (price / maxPrice) * chartHeight;
                const isCurrent = idx === currentStepData.currentIndex;
                const isBuy = idx === currentStepData.buyDay;
                const isBestBuy = idx === currentStepData.bestBuyDay;
                const isBestSell = idx === currentStepData.bestSellDay;

                let bgColor = "bg-gray-300";
                if (isBestBuy || isBestSell) bgColor = "bg-green-500";
                else if (isBuy) bgColor = "bg-blue-500";

                return (
                  <div
                    key={idx}
                    className="flex-1 flex flex-col items-center justify-end relative h-full"
                  >
                    <div
                      className={`w-full transition-all duration-300 ${
                        isCurrent ? "ring-2 ring-red-500" : ""
                      }`}
                      style={{
                        height: `${height}px`,
                        backgroundColor: bgColor,
                      }}
                    >
                      <div className="text-center text-xs -mt-4 font-bold text-gray-800">
                        {price}
                      </div>
                    </div>
                    <div className="text-xs mt-1">Day {idx}</div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 text-center">
              <div className="p-2 rounded bg-blue-100">
                <div className="text-xs text-blue-800 font-medium">
                  Min Price Seen (Buy Day)
                </div>
                <div className="font-bold text-lg text-blue-900">
                  {currentStepData.minPrice === Infinity
                    ? "-"
                    : `${currentStepData.minPrice} (Day ${currentStepData.buyDay})`}
                </div>
              </div>
              <div className="p-2 rounded bg-green-100">
                <div className="text-xs text-green-800 font-medium">
                  Max Profit Found
                </div>
                <div className="font-bold text-lg text-green-900">
                  ${currentStepData.maxProfit ?? 0}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The optimal solution involves a single pass through the `prices`
              array. We maintain two variables: `minPrice` (the lowest price
              seen so far) and `maxProfit`. As we iterate, we first check if the
              current price is a new minimum. Then, we calculate the potential
              profit if we were to sell on the current day (`price - minPrice`).
              If this profit is greater than our `maxProfit`, we update it.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The key is to track the lowest buy price found *so far* as you
                  iterate.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  This single-pass approach automatically ensures that you
                  always "buy" before you "sell".
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  You can't sell and then buy. By updating `minPrice` first,
                  you're always calculating profit based on a past or current
                  day's price.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuySellStockVisualizer;
